import { ResourceDetailTabProps } from '@ciandtspike.io/plugin-ciandtspike-common';
import { Card, EmptyState } from '@ciandtspike.io/plugin-ui';
import { CopyTextButton, Link } from '@backstage/core-components';
import { Typography, makeStyles } from '@material-ui/core';

interface EnvLike {
  name: string;
  projectId?: string;
  artifactRepository?: string;
  ci?: { audience?: string };
}

const DEFAULT_BRANCH = 'main';

const useStyles = makeStyles(theme => ({
  grid: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  title: {
    fontWeight: 700,
    margin: 0,
  },
  cloneRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.05)',
    borderRadius: 8,
    padding: theme.spacing(0.5, 0.5, 0.5, 1.5),
  },
  clone: {
    fontFamily: 'monospace',
    fontSize: 13,
    whiteSpace: 'nowrap',
    overflowX: 'auto',
    flex: 1,
    minWidth: 0,
  },
  meta: {
    color: theme.palette.text.secondary,
  },
  intro: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  envBlock: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1.5),
  },
  envTitle: {
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: theme.spacing(0.5),
  },
  varRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.25),
  },
  varKey: {
    fontFamily: 'monospace',
    fontSize: 12,
    fontWeight: 700,
  },
  varVal: {
    fontFamily: 'monospace',
    fontSize: 12,
    color: theme.palette.text.secondary,
    wordBreak: 'break-all',
  },
}));

export const RepositoryTab = ({ detail }: ResourceDetailTabProps) => {
  const classes = useStyles();
  const repoUrl = detail.repositoryUrl as string | null | undefined;
  const projectKey = (detail.rawConfig as Record<string, string> | undefined)
    ?.bitbucketProjectKey;
  const envs = (detail.environmentStatuses as EnvLike[] | undefined) ?? [];

  if (!repoUrl) {
    return (
      <EmptyState
        title="Repositório indisponível"
        description="Este recurso não expôs um repositório de código."
      />
    );
  }

  const cloneCommand = `git clone ${repoUrl}.git`;

  return (
    <div className={classes.grid} aria-label="Repositório">
      <Card>
        <Typography variant="subtitle1" component="h3" className={classes.title}>
          Repositório
        </Typography>
        <Typography variant="body2">
          <Link to={repoUrl} target="_blank" rel="noreferrer">
            {repoUrl}
          </Link>
        </Typography>

        <div className={classes.cloneRow}>
          <code className={classes.clone}>{cloneCommand}</code>
          <CopyTextButton text={cloneCommand} tooltipText="Comando copiado" />
        </div>

        <Typography variant="caption" className={classes.meta}>
          Branch padrão: {DEFAULT_BRANCH}
          {projectKey ? ` · Projeto: ${projectKey}` : ''}
        </Typography>
      </Card>

      {envs.length > 0 && (
        <Card>
          <Typography
            variant="subtitle1"
            component="h3"
            className={classes.title}
          >
            Próximos passos de deploy
          </Typography>
          <Typography variant="body2" className={classes.intro}>
            O deploy é feito pelo pipeline do repositório (Bitbucket). Para cada
            ambiente abaixo, configure estas variáveis no pipeline com os
            valores já provisionados:
          </Typography>
          {envs.map(env => (
            <div key={env.name} className={classes.envBlock}>
              <Typography variant="subtitle2" className={classes.envTitle}>
                {env.name}
              </Typography>
              <div className={classes.varRow}>
                <code className={classes.varKey}>
                  {env.name.toUpperCase()}_PROJECT_ID
                </code>
                <span className={classes.varVal}>{env.projectId ?? '—'}</span>
              </div>
              <div className={classes.varRow}>
                <code className={classes.varKey}>ARTIFACT_REGISTRY</code>
                <span className={classes.varVal}>
                  {env.artifactRepository ?? '—'}
                </span>
              </div>
              <div className={classes.varRow}>
                <code className={classes.varKey}>WIF_AUDIENCE</code>
                <span className={classes.varVal}>{env.ci?.audience ?? '—'}</span>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
};
